<template>
  <div class="passenger-form" v-if="fields.length > 0">
    <b-row>
      <template v-for="(field, field_index) in fields">
        <div
          v-if="field_index === 0"
          class="passenger-form__title col-12"
          :key="`field_static_0_${passenger_index}_${field_index}`"
        >
          <passenger-icon
            :passenger="passenger"
            :color="sass('gray-700')"
            :size="18"
          />
          <span>{{ `${field.label} ${passenger_index + 1}` }}</span>
          <span class="passenger-form__age"> ({{ age_label }}) </span>
        </div>

        <!-- Gender -->
        <div
          v-if="field.name === 'gender'"
          class="passenger-form__field col-6 col-lg-3 col-xl-2"
          :key="`field_static_${field.name}_${passenger_index}_${field_index}`"
        >
          <validation-provider
            ref="gender_provider"
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <div class="passenger-form__select">
              <vb-select
                :class="field.name"
                v-model="field.value"
                :vb_options="{
                  options: field.options,
                  persistent_key: build_input_id(passenger, field.name),
                }"
              />
            </div>
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Name -->
        <div
          v-else-if="field.name === 'name'"
          class="passenger-form__field col-12 col-lg-3"
          :key="`field_static_name_${passenger_index}_${field_index}`"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <vb-input
              v-model="field.value"
              :vb_options="{
                placeholder: field.placeholder,
                autocomplete: field.autocomplete,
                name: field.name,
                persistent_key: build_input_id(passenger, field.name),
              }"
            />
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Surname -->
        <div
          v-else-if="field.name === 'surname'"
          class="passenger-form__field col-12 col-lg-3"
          :key="`field_static_lastname_${passenger_index}_${field_index}`"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <vb-input
              v-model="field.value"
              :vb_options="{
                placeholder: field.placeholder,
                autocomplete: field.autocomplete,
                name: field.name,
                persistent_key: build_input_id(passenger, field.name),
              }"
            />
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Birthday -->
        <div
          v-else-if="field.name === 'birthday'"
          class="passenger-form__field col-12 col-lg-3 col-xl-2"
          :key="`field_static_birthdate_${passenger_index}_${field_index}`"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_static_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>
            <div class="passenger-form__calendar">
              <!-- Adults: native date input -->
              <div
                v-if="is_adult && !is_mobile()"
                class="passenger-form__birth"
              >
                <vb-input
                  v-model="field.value"
                  :vb_options="{
                    autocomplete: 'off',
                    placeholder: 'DD/MM/YYYY',
                    min: field.calendar.min,
                    max: field.calendar.max,
                    type: 'date',
                    value: field.value,
                    persistent_key: build_input_id(passenger, field.name),
                  }"
                  @update="(e) => (field.value = e)"
                />
              </div>
              <!-- Children/Infants: v-date-picker with age-constrained range -->
              <!-- TODO TAZ-549 https://salabam.atlassian.net/browse/TAZ-549
                   Custom header duplicated in LF (travelers-details-form.vue) and VV (passenger-form.vue).
                   Consider unifying with a shared <vb-date-picker> wrapper in src/components/library/. -->
              <template v-else>
                <div
                  class="passenger-form__birth-trigger cursor-pointer"
                  @click="
                    field.calendar.show_datepicker =
                      !field.calendar.show_datepicker
                  "
                >
                  <vb-icon :name="'calendar'" :size="20" />
                  <vb-input
                    :value="
                      field.value
                        ? field.value.split('-').reverse().join('/')
                        : ''
                    "
                    :vb_options="{
                      placeholder: 'DD/MM/YYYY',
                      readonly: true,
                    }"
                  />
                </div>
                <div v-show="field.calendar.show_datepicker" class="vc-wrapper">
                  <v-date-picker
                    :ref="`datepicker:${passenger_index}`"
                    :model-config="field.calendar.model_config"
                    :minDate="field.calendar.min"
                    :maxDate="field.calendar.max"
                    :initial-page="calendar_initial_page(field)"
                    v-model="field.value"
                    @input="
                      () => {
                        field.calendar.show_datepicker = false;
                      }
                    "
                  >
                    <template #header="page">
                      <div class="vc-header-custom">
                        <span class="vc-header-custom__month">{{
                          page.monthLabel
                        }}</span>
                        <span class="vc-header-custom__year">
                          <button
                            type="button"
                            class="vc-header-custom__arrow"
                            :class="{
                              'is-disabled': is_at_year_bound(-1, page, field.calendar),
                            }"
                            :disabled="is_at_year_bound(-1, page, field.calendar)"
                            @click="move_datepicker(-1, passenger_index, page, field.calendar)"
                          >
                            &#8249;
                          </button>
                          <span>{{ page.year }}</span>
                          <button
                            type="button"
                            class="vc-header-custom__arrow"
                            :class="{
                              'is-disabled': is_at_year_bound(1, page, field.calendar),
                            }"
                            :disabled="is_at_year_bound(1, page, field.calendar)"
                            @click="move_datepicker(1, passenger_index, page, field.calendar)"
                          >
                            &#8250;
                          </button>
                        </span>
                      </div>
                    </template>
                  </v-date-picker>
                </div>
              </template>
            </div>
            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>

        <!-- Generic required fields -->
        <div
          class="passenger-form__field col-12 col-lg-3 col-xl-2"
          :key="`field_${passenger_index}_${field_index}`"
          v-else-if="field.validate.required && !field.hidden"
        >
          <validation-provider
            :rules="field.validate"
            :name="field.label"
            v-slot="{ errors }"
            :vid="`vvp_${passenger_index}_${field_index}`"
          >
            <label class="passenger-form__label">
              <span>{{ field.label }}</span>
              <vb-icon
                v-if="field.tooltip"
                :name="'info'"
                :color="sass('gray-500')"
                :size="14"
                class="passenger-form__tooltip-icon"
                v-b-popover.hover.top="field.tooltip"
              />
            </label>

            <div
              v-if="field.type === 'calendar'"
              class="passenger-form__calendar"
            >
              <!-- Adults: native date input -->
              <div
                v-if="is_adult && !is_mobile()"
                class="passenger-form__birth"
              >
                <vb-input
                  :key="`input_${passenger_index}_${field_index}`"
                  v-model="field.value"
                  :vb_options="{
                    autocomplete: 'off',
                    placeholder: 'DD/MM/YYYY',
                    min: field.calendar.min,
                    max: field.calendar.max,
                    type: 'date',
                    value: field.value,
                    persistent_key: build_input_id(passenger, field.name),
                  }"
                  @update="(e) => (field.value = e)"
                />
              </div>
              <!-- Children/Infants + Mobile adults: v-date-picker with age-constrained range -->
              <template v-else>
                <div
                  class="passenger-form__birth-trigger cursor-pointer"
                  @click="
                    field.calendar.show_datepicker =
                      !field.calendar.show_datepicker
                  "
                >
                  <vb-icon :name="'calendar'" :size="20" />
                  <vb-input
                    :value="
                      field.value
                        ? field.value.split('-').reverse().join('/')
                        : ''
                    "
                    :vb_options="{
                      placeholder: 'DD/MM/YYYY',
                      readonly: true,
                    }"
                  />
                </div>
                <div v-show="field.calendar.show_datepicker" class="vc-wrapper">
                  <v-date-picker
                    :ref="`datepicker:generic:${passenger_index}:${field_index}`"
                    :model-config="field.calendar.model_config"
                    :minDate="field.calendar.min"
                    :maxDate="field.calendar.max"
                    :initial-page="calendar_initial_page(field)"
                    v-model="field.value"
                    @input="
                      () => {
                        field.calendar.show_datepicker = false;
                      }
                    "
                  >
                    <template #header="page">
                      <div class="vc-header-custom">
                        <span class="vc-header-custom__month">{{
                          page.monthLabel
                        }}</span>
                        <span class="vc-header-custom__year">
                          <button
                            type="button"
                            class="vc-header-custom__arrow"
                            :class="{
                              'is-disabled':
                                page.year <= parseInt(field.calendar.min),
                            }"
                            :disabled="
                              page.year <= parseInt(field.calendar.min)
                            "
                            @click="
                              move_datepicker_generic(
                                -12,
                                passenger_index,
                                field_index,
                              )
                            "
                          >
                            &#8249;
                          </button>
                          <span>{{ page.year }}</span>
                          <button
                            type="button"
                            class="vc-header-custom__arrow"
                            :class="{
                              'is-disabled':
                                page.year >= parseInt(field.calendar.max),
                            }"
                            :disabled="
                              page.year >= parseInt(field.calendar.max)
                            "
                            @click="
                              move_datepicker_generic(
                                12,
                                passenger_index,
                                field_index,
                              )
                            "
                          >
                            &#8250;
                          </button>
                        </span>
                      </div>
                    </template>
                  </v-date-picker>
                </div>
              </template>
            </div>

            <vb-input
              v-else-if="field.type === 'text'"
              :key="`input_${passenger_index}_${field_index}`"
              v-model="field.value"
              :vb_options="{
                placeholder: field.placeholder,
                autocomplete: field.autocomplete,
                name: field.name,
                persistent_key: build_input_id(passenger, field.name),
              }"
            />

            <div
              v-else-if="field.type === 'select'"
              class="passenger-form__select"
            >
              <vb-select
                :class="field.name === 'type_pax' ? 'vb-select--readonly' : ''"
                :key="`select_${passenger_index}_${field_index}`"
                v-model="field.value"
                :disabled="field.disabled"
                :vb_options="{
                  options: field.options,
                  persistent_key:
                    field.name !== 'type_pax'
                      ? build_input_id(passenger, field.name)
                      : undefined,
                }"
              /><span
                v-if="field.name === 'type_pax'"
                v-html="
                  passenger.age < 30
                    ? `(${passenger.age} ${passenger.age > 1 ? 'anni' : 'anno'})`
                    : ''
                "
              ></span>
            </div>

            <vb-checkbox
              v-else-if="field.type === 'checkbox'"
              v-model="field.value"
              :vb_options="{ text: field.placeholder }"
            />

            <span class="vee--errors">{{ errors[0] }}</span>
          </validation-provider>
        </div>
      </template>
    </b-row>
  </div>
</template>

<script>
import moment from "moment";
import { build_store_key } from "@/utilities/persistent-inputs";
import { is_mobile } from "@utilities/commons";
import { is_adult } from "@alpitour/utilities";
import { alpitour_rybbit_event } from "@alpitour/utilities/rybbit";
import { clamp_year_target, is_at_year_bound } from "@/utilities/calendar";
import { DEFAULT_ADULT_AGE } from "@resources/passenger";
import PassengerIcon from "@alpitour/components/atoms/passenger-icon.vue";

export default {
  name: "passenger-form",
  components: {
    "passenger-icon": PassengerIcon,
  },
  props: {
    passenger: {
      type: Object,
      required: true,
    },
    passenger_index: {
      type: Number,
      required: true,
    },
  },
  computed: {
    fields() {
      const { details = {} } = this.passenger || {};
      return details.fields || [];
    },
    age_category() {
      if (this.passenger.age <= 1) return "Neonato";
      if (this.passenger.age >= 18) return "Adulto";
      return "Bambino";
    },
    age_label() {
      if (this.passenger.age === DEFAULT_ADULT_AGE) return this.age_category;
      const unit = this.passenger.age > 1 ? "anni" : "anno";
      return `${this.age_category} ${this.passenger.age} ${unit}`;
    },
    is_adult() {
      return is_adult(this.passenger);
    },
    passenger_name() {
      return (this.fields.find((f) => f.name === "name") || {}).value;
    },
    passenger_surname() {
      return (this.fields.find((f) => f.name === "surname") || {}).value;
    },
    passenger_gender() {
      return (this.fields.find((f) => f.name === "gender") || {}).value;
    },
  },
  watch: {
    passenger_name() {
      this.maybe_trigger_gender_validation();
    },
    passenger_surname() {
      this.maybe_trigger_gender_validation();
    },
  },
  methods: {
    moment,
    is_mobile,
    build_store_key,
    build_input_id(passenger, field_name) {
      return this.build_store_key(
        passenger.id,
        passenger.passengerType,
        field_name,
      );
    },
    calendar_initial_page(field) {
      const max = field && field.calendar && field.calendar.max;
      if (max) {
        const date = new Date(max);
        if (!isNaN(date.getTime())) {
          return { year: date.getFullYear(), month: date.getMonth() + 1 };
        }
      }
      return { year: moment().year(), month: moment().month() + 1 };
    },
    is_at_year_bound,
    move_datepicker(years_delta, pIndex, page, bounds) {
      const ref = this.$refs[`datepicker:${pIndex}`];
      const picker = Array.isArray(ref) ? ref[0] : ref;
      if (!picker) return;
      const target = clamp_year_target(years_delta, page, bounds);
      picker.move({ month: target.month, year: target.year }).catch((e) => {
        this.log('[passenger-form] datepicker move failed', e);
        alpitour_rybbit_event("error:datepicker:fail", {
          context: "birthday",
          passenger_index: pIndex,
          error_message: String(e && e.message ? e.message : e),
        });
      });
    },
    move_datepicker_generic(months, pIndex, fIndex) {
      const ref = this.$refs[`datepicker:generic:${pIndex}:${fIndex}`];
      const picker = Array.isArray(ref) ? ref[0] : ref;
      if (picker) {
        picker.move(months).catch((e) => {
          this.log('[passenger-form] datepicker generic move failed', e);
          alpitour_rybbit_event("error:datepicker:fail", {
            context: "generic",
            passenger_index: pIndex,
            field_index: fIndex,
            error_message: String(e && e.message ? e.message : e),
          });
        });
      }
    },
    maybe_trigger_gender_validation() {
      if (
        this.passenger_name &&
        this.passenger_surname &&
        !this.passenger_gender
      ) {
        this.$nextTick(() => {
          const ref = this.$refs.gender_provider;
          const provider = Array.isArray(ref) ? ref[0] : ref;
          if (provider && typeof provider.validate === "function") {
            provider.validate();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.passenger-form {
  background-color: $white;
  border-radius: 5px;
  padding: $grid-gutter-width/2;

  &__field {
    margin-bottom: $grid-gutter-width/2;
  }

  &__title {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-weight: $vb-font-weight-semibold;
    margin-bottom: $grid-gutter-width/2;
    color: $gray-700;
    padding: ($grid-gutter-width/4) ($grid-gutter-width/2)
      ($grid-gutter-width/2);
  }

  &__age-detail {
    margin-left: 0.25rem;
  }

  &__label {
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    font-size: $font-size-sm;
    color: $gray-600;
  }

  &__tooltip-icon {
    margin-left: 0.25rem;
  }

  &__select {
    display: flex;
    align-items: center;

    .vb-select--readonly {
      width: auto;
      padding-right: $grid-gutter-width/10;
    }

    span {
      white-space: nowrap;
    }
  }

  &__birth-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    input {
      pointer-events: none;
      cursor: pointer;
      background: $white;
    }
  }

  .vc-wrapper {
    margin-top: 5px;
  }
}
</style>
